<template>
  <div class="scanner-result-page">
    <Header :HeaderTitle="scanInfo.scanning + ' scan'" :HeaderSubTitle="subtitle" :ShowBack="true" @back="back" />
    <main class="scan-result-layout">
      <p v-if="settingsError" class="scan-result__signature-error" role="alert">{{ settingsError }}</p>
      <section v-if="phase === 'loading'" class="scan-result-card" role="status">Checking code...</section>
      <section v-else-if="phase === 'review' || phase === 'signature'" class="scan-result-card scan-result-card--signature">
        <h1 class="scan-result__title">{{ phase === "signature" ? "Signature required" : "Complete check-in" }}</h1>
        <p class="scan-result__subtitle">{{ phase === "signature" ? "Ask the attendee to sign before check-in." : "A photo is optional. You can check in without one." }}</p>
        <SignaturePad v-if="phase === 'signature'" ref="signaturePad" @change="signatureReady = $event" />
        <PhotoCapture v-if="settings?.photoEnabled && !photoDisabled" @change="photo = $event" />
        <p v-if="error" class="scan-result__signature-error" role="alert">{{ error }}</p>
        <button type="button" class="btn btn--primary scan-again" :disabled="sending || (phase === 'signature' && !signatureReady)" @click="submit">
          {{ sending ? "Checking in..." : phase === "signature" ? "Sign and check in" : "Check in" }}
        </button>
      </section>
      <section v-else class="scan-result-card" :class="'scan-result-card--' + resultVariant" aria-labelledby="scan-result-heading">
        <div class="scan-result__message">
          <img v-if="status === 'CHECK-IN SUCCESSFUL'" src="../assets/images/scan/icon-scan-ok.svg" alt="" aria-hidden="true" class="scan-result__icon" />
          <img v-else-if="status === 'CHECK-IN DUPLICATE'" src="../assets/images/scan/icon-scan-duplicate.svg" alt="" aria-hidden="true" class="scan-result__icon" />
          <img v-else src="../assets/images/scan/icon-scan-denied.svg" alt="" aria-hidden="true" class="scan-result__icon" />
          <h1 id="scan-result-heading" class="scan-result__title" :class="titleColor">{{ resultTitle }}</h1>
          <p class="scan-result__subtitle">{{ resultMessage }}</p>
        </div>
        <div v-if="hasParticipant" class="scan-result__info">
          <div class="scan-result__info-label">Attendee</div>
          <div class="scan-result__info-name">{{ participantName }}</div>
          <div v-if="participant.Email" class="scan-result__info-detail">{{ participant.Email }}</div>
          <div v-if="participant.Company" class="scan-result__info-detail">{{ participant.Company }}</div>
        </div>
        <div v-if="eventName" class="scan-result__info">
          <div class="scan-result__info-label">Event</div>
          <div class="scan-result__info-event">{{ eventName }}</div>
        </div>
        <p v-if="error" class="scan-result__signature-error" role="alert">{{ error }}</p>
        <button v-if="phase === 'error'" type="button" class="btn btn--default" @click="submit">Try again</button>
      </section>
      <button v-if="phase === 'done' || phase === 'error'" type="button" class="btn btn--primary scan-again" @click="scan">
        <span>Scan</span><img src="../assets/images/scan/icon-btn-scan.svg" alt="" aria-hidden="true" />
      </button>
    </main>
  </div>
</template>

<script>
import Header from "./HeaderComponent.vue";
import SignaturePad from "./SignaturePadComponent.vue";
import PhotoCapture from "./PhotoCaptureComponent.vue";

export default {
  name: "ScannedResultComponent",
  components: { Header, SignaturePad, PhotoCapture },
  props: { QrValue: String },
  data: () => ({ phase: "loading", response: null, error: "", sending: false, photo: null, photoDisabled: false, signatureReady: false }),
  computed: {
    scanInfo() { return this.$store.getters.getScanInfo || {}; },
    settings() { return this.$store.getters.getCheckInSettings; },
    settingsError() { return this.$store.getters.getCheckInSettingsError; },
    subtitle() { return this.scanInfo.scanning === "Session" ? this.$store.getters.getProgramName : this.$store.getters.getEventName; },
    eventName() { return this.$store.getters.getEventName; },
    participant() { return this.response?.data?.EventParticipant || {}; },
    hasParticipant() { return !!(this.participant.FirstName || this.participant.LastName || this.participant.Email); },
    participantName() { return [this.participant.FirstName, this.participant.LastName].filter(Boolean).join(" ") || this.participant.Email || "Attendee"; },
    status() { return this.response?.data?.QRStatus || "SCAN ERROR"; },
    resultTitle() { return this.status === "CODE INVALID" ? "CHECK-IN DENIED" : this.status; },
    resultMessage() { return this.response?.data?.Msg || ({ "CHECK-IN SUCCESSFUL": "Attendee checked in", "CHECK-IN DUPLICATE": "Already checked in", "CODE INVALID": "This code does not resolve to a participant.", "SCAN ERROR": "We could not process this QR code." })[this.status] || ""; },
    resultVariant() { return this.status === "CODE INVALID" ? "invalid" : this.phase === "error" ? "error" : "detailed"; },
    titleColor() { return this.status === "CHECK-IN SUCCESSFUL" ? "scan-result__title--success" : this.status === "CHECK-IN DUPLICATE" ? "scan-result__title--duplicate" : "scan-result__title--denied"; },
  },
  methods: {
    back() { this.scan(); this.$router.back(); },
    scan() { this.$store.commit("setScanned", { isScanned: false }); },
    async submit() {
      if (this.sending) return;
      this.sending = true;
      this.error = "";
      try {
        const signature = this.phase === "signature" ? this.$refs.signaturePad.image() : null;
        if (this.phase === "signature" && !signature) { this.error = "Please sign before checking in."; return; }
        const url = process.env.VUE_APP_ROOT_API + "/v1/DataAPI/ScanEventQRcode/json?" + new URLSearchParams({ EventID: this.scanInfo.eventId, ...(this.scanInfo.programId == null ? {} : { ProgramID: this.scanInfo.programId }), qrValue: this.QrValue });
        const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ signature, photo: this.photo }) });
        if (!res.ok) throw new Error("Unable to check in. Please try again.");
        const result = await res.json();
        if (result.data?.ErrorCode === "SIGNATURE_REQUIRED") { this.phase = "signature"; return; }
        if (result.data?.ErrorCode === "PHOTO_NOT_ENABLED") { this.photo = null; this.photoDisabled = true; this.error = result.data.Msg; if (this.phase !== "signature") this.phase = "review"; return; }
        if (["IMAGE_INVALID", "IMAGE_TOO_LARGE"].includes(result.data?.ErrorCode)) { this.error = result.data.Msg; return; }
        this.response = result;
        this.photo = null;
        this.phase = result.data?.QRStatus === "SCAN ERROR" ? "error" : "done";
      } catch (error) {
        this.error = error.message || "Unable to check in. Please try again.";
        this.phase = this.phase === "signature" || this.phase === "review" ? this.phase : "error";
      } finally { this.sending = false; }
    },
  },
  async mounted() {
    try { await this.$store.dispatch("loadCheckInSettings", this.scanInfo.eventId); } catch (_) { /* The server still enforces signature rules. */ }
    if (this.settings?.photoEnabled) this.phase = "review";
    else { this.phase = "loading"; this.submit(); }
  },
};
</script>
